import type { AuthChangeEvent } from '@supabase/supabase-js';
import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '../supabase-client';
import type { AuthState } from '../models/types';
import Loading from '../pages/Loading';

type UserRole = 'tranding_admin' | 'merchant_admin' | 'employee' | null;

type AuthContextType = {
  userEmail: string;
  userRole: UserRole;
  setUserRole: React.Dispatch<React.SetStateAction<UserRole>>;
  merchantId: number | undefined;
  merchantName: string;
  loggedIn: AuthState;
  handleLogin: (
    email: string | undefined,
    password: string | undefined,
  ) => Promise<void>;
  handleLogout: () => Promise<void>;
};

export const checkIsLoggedIn = async (): Promise<boolean> => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Error checking session:', error);
    return false;
  }
  return !!data.session;
};

export const AuthContext = createContext<AuthContextType>({
  userEmail: '',
  userRole: null,
  setUserRole: () => {},
  merchantId: undefined,
  merchantName: '',
  loggedIn: 'waiting',
  handleLogin: async () => undefined,
  handleLogout: async () => {},
});

export const AuthContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>('waiting');
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [merchantId, setMerchantId] = useState<number>();
  const [merchantName, setMerchantName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');

  const handleSuccessfulLogin = async () => {
    const userId = (await supabase.auth.getUser()).data.user?.id;

    const userDataResponse = await supabase
      .schema('admin_backend')
      .from('backend_users_view')
      .select('*')
      .eq('id', userId);

    if (userDataResponse.error) throw new Error(userDataResponse.error.message);
    if (!userDataResponse.data) throw new Error('No user data found');

    const userRole = userDataResponse.data[0].user_role;
    setAuthState('logged_in');
    setUserRole(userRole);
    setMerchantId(userDataResponse.data[0].merchant_id);
    setMerchantName(userDataResponse.data[0].merchant_name);
    setUserEmail(userDataResponse.data[0].email);
  };

  const handleLogin = async (
    email: string | undefined,
    password: string | undefined,
  ): Promise<void> => {
    // const email = (
    //   document.querySelector('input[name="email"]') as HTMLInputElement
    // )?.value;

    if (!email || !password) {
      throw Error('Email and password are required');
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Error logging in');
      console.error(error);
      throw Error(error.message);
    }

    if (!data) {
      console.error('Data is null in login response');
      console.error(data);
      throw Error('No data returned from login');
    }

    if (!data.session) {
      console.error('Session is null in login data');
      console.error(data.session);
      throw Error('No session found in login data');
    }
    console.log(data.user.role);

    await handleSuccessfulLogin();
  };

  const handleLogout = async () => {
    setAuthState('waiting');
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error logging out:', error);
      // Only setAuthState and setUserRole if error, because if successful, supabase.auth.onAuthStateChange listenere already sets these
      setAuthState('logged_out');
      setUserRole(null);
      return;
    }
    console.log('Logged out successfully');
  };

  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session) => {
        console.log(`authstatechange: ${event}`);

        switch (event) {
          case 'INITIAL_SESSION':
            // Triggers on attaching onAuthStateChange listener, but user might or might not be signed in. To tell if user is signed in, check session. If null, user is not signed in.
            // Also triggers when user signs in
            if (!session) {
              console.log('No session.');
              setAuthState('logged_out');
              break;
            }

            if (!session.user) {
              console.log('No user.');
              setAuthState('logged_out');
              break;
            }

            if (session.expires_at! && session.expires_at * 1000 > Date.now()) {
              handleSuccessfulLogin();
            }

            break;
          case 'PASSWORD_RECOVERY':
            // supabase's password recovery link sent to the user's email redirects to tranding and emits a PASSWORD_RECOVERY event
            window.location.assign('/reset-password');
            break;
          case 'SIGNED_IN':
            handleSuccessfulLogin();
            break;
          case 'SIGNED_OUT':
            setAuthState('logged_out');
            setUserRole(null);
            break;
          case 'TOKEN_REFRESHED':
            console.log('Token refreshed');
            handleSuccessfulLogin();
            break;
          case 'USER_UPDATED':
            // e.g. user changes email or password
            break;
          default:
            setAuthState('logged_out');
            setUserRole(null);
            break;
        }
      },
    );

    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={useMemo(
        () => ({
          userEmail,
          userRole,
          setUserRole,
          merchantId,
          merchantName,
          loggedIn: authState,
          handleLogin,
          handleLogout,
        }),
        [
          userEmail,
          userRole,
          setUserRole,
          authState,
          handleLogin,
          handleLogout,
        ],
      )}>
      {authState === 'waiting' ? <Loading /> : children}
    </AuthContext.Provider>
  );
};
